import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Trash2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const WishlistPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchWishlist(); }, []);

  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist/${productId}`, { withCredentials: true });
      toast.success('Removed from wishlist');
      fetchWishlist();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const addToCart = async (productId) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />

      <div className="container mx-auto px-4 py-12 pt-20">
        <h1 className="text-4xl font-black font-heading mb-8" data-testid="wishlist-heading">Wishlist</h1>

        {products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4" data-testid="empty-wishlist-message">Your wishlist is empty</p>
            <Button onClick={() => navigate('/products')} className="rounded-full" data-testid="browse-products-button">Browse Teas</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.product_id} className="group" data-testid={`wishlist-item-${product.product_id}`}>
                <div className="relative rounded-2xl bg-white border border-[#E0E0E0] overflow-hidden mb-3 hover:-translate-y-1 hover:shadow-lg transition-all">
                  <div className="aspect-square overflow-hidden cursor-pointer" onClick={() => navigate(`/products/${product.product_id}`)}>
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <button onClick={() => removeFromWishlist(product.product_id)} className="absolute top-3 right-3 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors" data-testid={`remove-wishlist-${product.product_id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{product.category}</p>
                <h3 className="font-bold text-sm font-heading mb-1">{product.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-bold">{'\u20B9'}{product.price}</span>
                  <Button size="sm" className="rounded-full h-8 px-3 text-xs" onClick={() => addToCart(product.product_id)} data-testid={`add-to-cart-wishlist-${product.product_id}`}>
                    <ShoppingCart className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default WishlistPage;
